import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownToLine,
  Box,
  Loader2,
  Minus,
  Pencil,
  Plus,
  RotateCcw,
  Scissors,
  Trash2,
  X,
} from 'lucide-react'
import { clearFloorAsset, setFloorAsset } from '@/lib/api/spaces.js'
import { useSpatialStore } from '@/features/spatial-model/store.js'
import { hasFloorOverride, resolveModelAsset } from '@/features/spatial-model/resolveModelAsset.js'
import { MODEL_PRESETS, inferModelAssetType } from '@/lib/modelPresets.js'

export default function FloorAssetPanel() {
  const floor = useSpatialStore((s) => s.floor)
  const setFloor = useSpatialStore((s) => s.setFloor)
  const roofClipFraction = useSpatialStore((s) => s.roofClipFraction)
  const setRoofClipFraction = useSpatialStore((s) => s.setRoofClipFraction)
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)

  const asset = resolveModelAsset(floor)
  const isFloorOverride = hasFloorOverride(floor)
  const sourceLabel =
    asset?.source === 'building'
      ? 'Inherited from building'
      : asset?.source === 'site'
      ? 'Inherited from site'
      : asset
      ? 'Floor override'
      : null
  const elevation = Number(asset?.transform?.elevation ?? 0)
  const originX = Number(asset?.transform?.origin_x ?? 0)
  const originY = Number(asset?.transform?.origin_y ?? 0)
  const northRotation = Number(asset?.transform?.north_rotation ?? 0)
  const scaleValue = Number(asset?.transform?.scale ?? 1)
  const roofClipPercent = Math.round((1 - roofClipFraction) * 100)

  const setMutation = useMutation({
    mutationFn: (values) => setFloorAsset(floor.id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor', floor.id] })
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
      setDialogOpen(false)
    },
  })

  const saveTimer = useRef(null)
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current) }, [])

  const applyTransformPatch = (patch) => {
    if (!asset || !floor) return
    const sanitized = {}
    for (const [key, raw] of Object.entries(patch)) {
      const num = Number(raw)
      sanitized[key] = Number.isFinite(num) ? num : 0
    }
    const nextTransform = { ...(asset.transform || {}), ...sanitized }
    const existing = (floor.assets || []).find((a) => a.id === asset.id)
    const patchedAssets = existing
      ? (floor.assets || []).map((a) =>
          a.id === asset.id ? { ...a, transform: nextTransform } : a,
        )
      : [
          ...(floor.assets || []),
          {
            id: `local-${asset.asset_type}`,
            asset_type: asset.asset_type,
            local_url: asset.local_url,
            file_name: asset.file_name,
            bucket: asset.bucket,
            storage_key: asset.storage_key,
            transform: nextTransform,
          },
        ]
    setFloor({ ...floor, assets: patchedAssets })
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      setMutation.mutate({
        asset_type: asset.asset_type,
        local_url: asset.local_url,
        file_name: asset.file_name,
        transform: nextTransform,
      })
    }, 350)
  }

  const applyElevation = (next) => applyTransformPatch({ elevation: next })
  const nudge = (delta) => applyElevation(roundTo(elevation + delta, 4))
  const resetTransform = () =>
    applyTransformPatch({
      origin_x: 0,
      origin_y: 0,
      elevation: 0,
      north_rotation: 0,
      scale: 1,
    })

  const clearMutation = useMutation({
    mutationFn: () => clearFloorAsset(floor.id, asset?.asset_type || 'fbx'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['floor', floor.id] })
      queryClient.invalidateQueries({ queryKey: ['orgs'] })
    },
  })

  if (!floor) return null

  const handleRemove = () => {
    if (!asset) return
    if (window.confirm(`Remove the 3D model "${asset.file_name || asset.local_url}" from this floor?`)) {
      clearMutation.mutate()
    }
  }

  return (
    <div className="inspector__group">
      <div className="inspector__group-title">3D Model</div>
      {asset ? (
        <div className="col gap-8" style={{ marginTop: 4 }}>
          <div className="row" style={{ gap: 8 }}>
            <Box size={16} color="var(--text-muted)" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {asset.file_name || 'Asset'}
              </div>
              <div className="muted" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                {asset.local_url || `${asset.bucket}/${asset.storage_key}`}
              </div>
              {sourceLabel && (
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sourceLabel}</div>
              )}
            </div>
          </div>
          <div className="muted" style={{ fontSize: 11 }}>
            scale {asset.transform?.scale ?? 1} · rot {asset.transform?.north_rotation ?? 0}° · y {fmt(elevation)}
          </div>

          <div className="col gap-8" style={{ marginTop: 2 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Elevation (m)</span>
              <span className="muted" style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                {fmt(elevation)}
              </span>
            </div>
            <div className="row" style={{ gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => nudge(-0.05)}
                title="Lower by 0.05 m"
              >
                <Minus size={12} />
              </button>
              <input
                type="number"
                step="0.01"
                value={elevation}
                onChange={(e) => {
                  const v = parseFloat(e.target.value)
                  applyElevation(Number.isFinite(v) ? v : 0)
                }}
                style={{ width: 80 }}
              />
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => nudge(0.05)}
                title="Raise by 0.05 m"
              >
                <Plus size={12} />
              </button>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => applyElevation(0)}
                title="Reset to 0"
                disabled={Math.abs(elevation) < 1e-6}
              >
                <ArrowDownToLine size={12} /> 0
              </button>
            </div>
            <input
              type="range"
              min={-15}
              max={15}
              step={0.05}
              value={clamp(elevation, -15, 15)}
              onChange={(e) => applyElevation(parseFloat(e.target.value))}
              style={{ accentColor: 'var(--accent)' }}
            />
            <span className="muted" style={{ fontSize: 11 }}>
              Negative values lower the model. To put the main floor of a multi-storey scan at ground
              level, set elevation to <code>-(basement&nbsp;height)</code> (e.g. <code>-2.4</code>).
            </span>
          </div>

          <div className="col gap-8" style={{ marginTop: 2 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Position &amp; rotation</span>
              <button
                type="button"
                className="btn btn--sm"
                onClick={resetTransform}
                title="Reset all transform fields"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>

            <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
              <span className="muted" style={{ fontSize: 11, width: 28 }}>Yaw</span>
              <input
                type="range"
                min={-180}
                max={180}
                step={1}
                value={clamp(northRotation, -180, 180)}
                onChange={(e) => applyTransformPatch({ north_rotation: parseFloat(e.target.value) })}
                style={{ accentColor: 'var(--accent)', flex: 1 }}
              />
              <input
                type="number"
                step="1"
                value={northRotation}
                onChange={(e) => applyTransformPatch({ north_rotation: parseFloat(e.target.value) })}
                style={{ width: 70 }}
              />
              <span className="muted" style={{ fontSize: 11 }}>°</span>
            </div>

            <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
              <span className="muted" style={{ fontSize: 11, width: 28 }}>X</span>
              <input
                type="number"
                step="0.1"
                value={originX}
                onChange={(e) => applyTransformPatch({ origin_x: parseFloat(e.target.value) })}
                style={{ width: 90 }}
              />
              <span className="muted" style={{ fontSize: 11, width: 28, marginLeft: 12 }}>Z</span>
              <input
                type="number"
                step="0.1"
                value={originY}
                onChange={(e) => applyTransformPatch({ origin_y: parseFloat(e.target.value) })}
                style={{ width: 90 }}
              />
              <span className="muted" style={{ fontSize: 11 }}>m</span>
            </div>

            <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
              <span className="muted" style={{ fontSize: 11, width: 28 }}>Scale</span>
              <input
                type="range"
                min={0.1}
                max={5}
                step={0.01}
                value={clamp(scaleValue, 0.1, 5)}
                onChange={(e) => applyTransformPatch({ scale: parseFloat(e.target.value) })}
                style={{ accentColor: 'var(--accent)', flex: 1 }}
              />
              <input
                type="number"
                step="0.01"
                min="0.001"
                value={scaleValue}
                onChange={(e) => applyTransformPatch({ scale: parseFloat(e.target.value) })}
                style={{ width: 70 }}
              />
              <span className="muted" style={{ fontSize: 11 }}>×</span>
            </div>

            <span className="muted" style={{ fontSize: 11 }}>
              Edits autosave to this floor as an override. Yaw rotates around vertical, X/Z slide the
              model horizontally on the ground plane.
            </span>
          </div>

          <div className="col gap-8" style={{ marginTop: 2 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span className="row" style={{ gap: 6, fontSize: 12, fontWeight: 500 }}>
                <Scissors size={12} /> Roof clip
              </span>
              <span className="muted" style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                {roofClipPercent}% off
              </span>
            </div>
            <input
              type="range"
              min={0.2}
              max={1}
              step={0.02}
              value={roofClipFraction}
              onChange={(e) => setRoofClipFraction(parseFloat(e.target.value))}
              style={{ accentColor: 'var(--accent)' }}
              aria-label="Roof clip amount"
            />
            <span className="muted" style={{ fontSize: 11 }}>
              Slide right to show the full model; left to cut more off the top.
            </span>
          </div>

          <div className="row" style={{ gap: 6 }}>
            <button type="button" className="btn btn--sm" onClick={() => setDialogOpen(true)}>
              <Pencil size={14} /> {isFloorOverride ? 'Edit' : 'Override on this floor'}
            </button>
            {isFloorOverride && (
              <button
                type="button"
                className="btn btn--sm"
                onClick={handleRemove}
                disabled={clearMutation.isPending}
              >
                <Trash2 size={14} /> Remove override
              </button>
            )}
          </div>
          {!isFloorOverride && (
            <span className="muted" style={{ fontSize: 11 }}>
              Editing elevation here will create a floor-only override.
            </span>
          )}
          {clearMutation.error && (
            <div className="auth-error" role="alert">
              <span>{clearMutation.error.uiMessage || 'Failed to remove asset.'}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="col gap-8" style={{ marginTop: 4 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            No 3D model is set for this floor. Add one to render the space in 3D.
          </div>
          <button type="button" className="btn btn--sm btn--primary" onClick={() => setDialogOpen(true)}>
            <Pencil size={14} /> Add 3D model
          </button>
        </div>
      )}

      {dialogOpen && (
        <FloorAssetDialog
          asset={asset}
          submitting={setMutation.isPending}
          error={setMutation.error?.uiMessage || null}
          onClose={() => {
            setMutation.reset()
            setDialogOpen(false)
          }}
          onSubmit={(values) => setMutation.mutate(values)}
        />
      )}
    </div>
  )
}

function FloorAssetDialog({ asset, submitting, error, onClose, onSubmit }) {
  const [values, setValues] = useState(() => ({
    local_url: asset?.local_url || '',
    file_name: asset?.file_name || '',
    asset_type: asset?.asset_type || 'fbx',
    scale: asset?.transform?.scale ?? '',
    origin_x: asset?.transform?.origin_x ?? '',
    origin_y: asset?.transform?.origin_y ?? '',
    elevation: asset?.transform?.elevation ?? '',
    north_rotation: asset?.transform?.north_rotation ?? '',
  }))
  const selectedPreset = MODEL_PRESETS.find(
    (preset) => preset.payload?.local_url === values.local_url,
  )

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const update = (field, value) => setValues((current) => ({ ...current, [field]: value }))

  const setPreset = (presetId) => {
    const preset = MODEL_PRESETS.find((item) => item.id === presetId)
    if (!preset?.payload) return
    setValues((current) => ({
      ...current,
      local_url: preset.payload.local_url,
      file_name: preset.payload.file_name || '',
      asset_type: preset.payload.asset_type || inferModelAssetType(preset.payload.file_name),
      scale: preset.payload.transform?.scale !== undefined && preset.payload.transform.scale !== 1
        ? String(preset.payload.transform.scale)
        : current.scale,
      north_rotation: preset.payload.transform?.north_rotation
        ? String(preset.payload.transform.north_rotation)
        : current.north_rotation,
      origin_x: preset.payload.transform?.origin_x
        ? String(preset.payload.transform.origin_x)
        : current.origin_x,
      origin_y: preset.payload.transform?.origin_y
        ? String(preset.payload.transform.origin_y)
        : current.origin_y,
      elevation: preset.payload.transform?.elevation
        ? String(preset.payload.transform.elevation)
        : current.elevation,
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!values.local_url.trim() || submitting) return
    const payload = {
      local_url: values.local_url.trim(),
      file_name: values.file_name.trim() || undefined,
      asset_type: values.asset_type,
      transform: {},
    }
    for (const key of ['scale', 'origin_x', 'origin_y', 'elevation', 'north_rotation']) {
      const raw = values[key]
      if (raw === '' || raw === null || raw === undefined) continue
      const num = Number(raw)
      if (Number.isFinite(num)) payload.transform[key] = num
    }
    onSubmit(payload)
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="asset-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id="asset-dialog-title" className="modal__title">
            {asset ? 'Replace 3D model' : 'Add 3D model'}
          </h2>
          <button type="button" className="icon-btn icon-btn--sm" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span className="auth-field__label">Asset preset</span>
            <select
              value={selectedPreset?.id || ''}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="">Custom URL or path...</option>
              {MODEL_PRESETS.filter((preset) => preset.payload).map((preset) => (
                <option key={preset.id} value={preset.id}>{preset.label}</option>
              ))}
            </select>
          </label>

          <label className="auth-field">
            <span className="auth-field__label">URL or path</span>
            <input
              autoFocus
              required
              value={values.local_url}
              onChange={(e) => update('local_url', e.target.value)}
              placeholder="/models/example.usdz or https://..."
            />
            <span className="muted" style={{ fontSize: 11 }}>
              Static asset path (e.g. <code>/models/foo.usdz</code>) or a public URL the browser can fetch.
            </span>
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Filename</span>
            <input
              value={values.file_name}
              onChange={(e) => update('file_name', e.target.value)}
              placeholder="auto"
            />
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Type</span>
            <select value={values.asset_type} onChange={(e) => update('asset_type', e.target.value)}>
              <option value="fbx">FBX</option>
              <option value="glb">GLB / glTF</option>
              <option value="usdz">USDZ / USD</option>
            </select>
          </label>

          <div className="inspector__group-title" style={{ marginTop: 4 }}>Transform</div>

          <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
            <label className="auth-field" style={{ flex: 1 }}>
              <span className="auth-field__label">Scale</span>
              <input
                type="number"
                step="0.001"
                value={values.scale}
                onChange={(e) => update('scale', e.target.value)}
                placeholder="1"
              />
            </label>
            <label className="auth-field" style={{ flex: 1 }}>
              <span className="auth-field__label">North rotation°</span>
              <input
                type="number"
                step="1"
                value={values.north_rotation}
                onChange={(e) => update('north_rotation', e.target.value)}
                placeholder="0"
              />
            </label>
          </div>

          <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
            <label className="auth-field" style={{ flex: 1 }}>
              <span className="auth-field__label">Origin X</span>
              <input
                type="number"
                step="0.1"
                value={values.origin_x}
                onChange={(e) => update('origin_x', e.target.value)}
                placeholder="0"
              />
            </label>
            <label className="auth-field" style={{ flex: 1 }}>
              <span className="auth-field__label">Origin Y</span>
              <input
                type="number"
                step="0.1"
                value={values.origin_y}
                onChange={(e) => update('origin_y', e.target.value)}
                placeholder="0"
              />
            </label>
            <label className="auth-field" style={{ flex: 1 }}>
              <span className="auth-field__label">Elevation</span>
              <input
                type="number"
                step="0.1"
                value={values.elevation}
                onChange={(e) => update('elevation', e.target.value)}
                placeholder="0"
              />
            </label>
          </div>

          <span className="muted" style={{ fontSize: 11 }}>
            Leave a transform field blank to keep the existing value (or use defaults on add).
          </span>

          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !values.local_url.trim()}>
              {submitting ? (
                <><Loader2 size={14} className="spin" /> Saving...</>
              ) : asset ? 'Replace model' : 'Add model'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function fmt(value) {
  return Number.isFinite(value) ? value.toFixed(2) : '0.00'
}

function roundTo(value, decimals) {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

function clamp(value, min, max) {
  if (!Number.isFinite(value)) return 0
  return Math.max(min, Math.min(max, value))
}
